import { memo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Modal from "../../../../../../components/Modal";
import { PlusTag } from "../../../../../../components/PlusTag";
import { useSite } from "../../../../../../contexts/Site";
import { useCurrentPage, withPage } from "../../../../../../hooks/CurrentPage";
import { getPageSections } from "../../../../../../model/pages";
import { classNames } from "../../../../../../utils/styles";
import { availableSections, SectionDefinition } from "../../sections";
import { SectionPreview } from "../SectionPreview";
import { SectionGrid } from "./styles";

// PLUS sections are freely usable while drafting regardless of subscription (the
// PLUS offer is only enforced at publish time - see model/plusFeatures.ts and the
// `plusRequired` review loop), so a section tile is only ever inactive because it's
// a single-use section that's already in the draft.
// Each section is a tile with an illustration of how it looks on the Keepo.
const SectionTile = ({ type, subType, multiple, pro }: SectionDefinition) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { site } = useSite();
  const { pageId } = useCurrentPage();
  // "already added" counts on the page being edited
  const sections = getPageSections(site, pageId);

  const safeSubType = subType ? `.${subType}` : "";
  const safeSubTypePath = subType ? `/${subType}` : "";
  const alreadyUsed = !!sections.find(section => section.type === type && section.subType === subType);
  const active = multiple || !alreadyUsed;

  return (
    <button
      type="button"
      className={classNames("tile", { inactive: !active, plus: pro })}
      aria-disabled={!active}
      onClick={() => active && navigate(withPage(`/editor/${type}${safeSubTypePath}`, pageId))}>
      <span className="preview">
        <SectionPreview type={type} subType={subType} />
        {pro && <PlusTag className="plus-tag" />}
        {!active && <span className="used">{t("pages.editor.sections.actions.add.modal.alreadyAdded")}</span>}
      </span>
      <span className="info">
        <span className="name">{t(`site.sections.${type}${safeSubType}`)}</span>
        <span className="description">
          {t(`pages.editor.sections.actions.add.modal.descriptions.${type}${subType ? `-${subType}` : ""}`)}
        </span>
      </span>
    </button>
  );
};

const MemoizedSectionTile = memo(SectionTile);

type Props = {
  onClose: () => void;
};

export const AddSectionModal = ({ onClose }: Props) => {
  const { t } = useTranslation();

  return (
    <Modal
      size="lg"
      divided
      outsideExit
      onExit={onClose}
      title={t("pages.editor.sections.actions.add.modal.title")}
      subtitle={t("pages.editor.sections.actions.add.modal.subtitle")}>
      <SectionGrid>
        <div className="grid">
          {availableSections.map(section => (
            <MemoizedSectionTile {...section} key={`${section.type}-${section.subType}`} />
          ))}
        </div>
      </SectionGrid>
    </Modal>
  );
};
